import { useState } from "react";
import { Asset } from "../models/asset";
import { fetchMaintenance, IAssetSummary } from "../api/fetchMaintenance";
import { fetchComplementaryAssetsDetails, fetchMailaAssets } from "../api/fetchAssets";
import { fetchCheckupsByChassisList } from "../api/fetchCheckup";
import { fetchVehiclesFromOccurrences } from "../api/fetchVehiclesFromOccurrences";

function useMaintenance(ids?: string[]) {
    const [firstLoad, setFirstLoad] = useState<boolean>(true);

    const [assetIds, setAssetIds] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [loadMoreLoading, setLoadMoreLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);

    const [allSummaryAssets, setAllSummaryAssets] = useState<IAssetSummary[]>([]);
    const [assets, setAssets] = useState<Asset[]>([]);
    const [assetIdsPaged, setAssetIdsPaged] = useState<IAssetSummary[][]>([]);

    const [disableBigNumber, setDisableBigNumber] = useState<boolean>(false);

    const loadAssetsPaged = async (filteredAssetIds?: string[], filteredByAssetTree?: boolean, allAssets?: Asset[]) => {
        if (!allAssets?.length) {
            setLoading(false);
            return
        };
        const fetchedAssets = allAssets ?? [];

        const assetIds = filteredAssetIds !== undefined && filteredAssetIds !== null ? filteredAssetIds : fetchedAssets.map(asset => asset.id);
        setAssetIds(assetIds);

        const assetIdSet = new Set(assetIds);
        const assetsSummary = fetchedAssets
            .filter(asset => assetIdSet.has(asset.id))
            .map(asset => ({
                id: asset.id,
                identification: asset.chassis,
                name: asset.name ?? "",
            }));

        // if(filteredByAssetTree) setDisableBigNumber(fetchedAssets.length !== assetsSummary.length);

        setAllSummaryAssets(assetsSummary);
        const chunkSize = 50;
        const pagedIds = [];
        for (let i = 0; i < assetIds.length; i += chunkSize) {
            pagedIds.push(assetsSummary.slice(i, i + chunkSize));
        }

        setAssetIdsPaged(pagedIds);
        setPage(0);
        getAssets(pagedIds, 0);
    }

    const getAssets = async (pagedIds: IAssetSummary[][], localPage?: number) => {
        // if(!pagedIds[localPage ?? page]?.length) return;
        setLoading(true);
        const [data,] = await fetchMaintenance(pagedIds[localPage ?? page]);
        setAssets(data);
        (!firstLoad || data.length > 0) && setLoading(false);
        data.length > 0 && setFirstLoad(false);

        addLicensePlateAndAccountName(data);
        addCheckups(data);
    }

    const getMoreAssets = async () => {
        setPage(page + 1);
        setLoadMoreLoading(true);
        const [data,] = await fetchMaintenance(assetIdsPaged[page + 1]);
        setAssets([...assets, ...data]);
        setLoadMoreLoading(false);

        addLicensePlateAndAccountName([...assets, ...data]);
        addCheckups(data);
    }

    const addLicensePlateAndAccountName = async (data: Asset[]) => {
        const assetIds = data.map(asset => asset.id);
        const chassisList = data.map(asset => asset.chassis);
        const [
            complementaryAssetsDetails,
            mailaAssets,
            vehiclesFromOccurrences
        ] = await Promise.all([
            fetchComplementaryAssetsDetails(assetIds),
            fetchMailaAssets(assetIds),
            fetchVehiclesFromOccurrences(chassisList) 
        ]);

        setAssets(prev => prev.map((asset, index) => {
            const complementaryData = complementaryAssetsDetails.find(el => el.id === asset.id);
            const occurrenceVehicle = vehiclesFromOccurrences.find(v => v.chassis === asset.chassis);
            let finalLicensePlate = asset.licensePlate;
            if (!finalLicensePlate || finalLicensePlate === '-') {
                finalLicensePlate = complementaryData?.licensePlate || '';
            }
            if (!finalLicensePlate || finalLicensePlate === '-') {
                finalLicensePlate = occurrenceVehicle?.licensePlate || '';
            }
            if (!finalLicensePlate || finalLicensePlate === '-') {
                finalLicensePlate = '-';
            }

            return {
                ...asset,
                accountName: complementaryData?.accountName || '-',
                licensePlate: finalLicensePlate,
                accountAssetId: complementaryData?.accountAssetId ?? asset.id,
                // Get maila engine hours if hourmeter not exists in asset
                hourmeter: asset.hourmeter ?? (() => {
                    const engineHours = mailaAssets.find(el => el.vehicleId === asset.id)?.engineHours;
                    if (engineHours) {
                        const [hh] = engineHours.split(':');
                        return Number(hh);
                    }
                    return 0;
                })(),
                // Get maila engine hours last update if lastHourmeterUpdate not exists in asset
                lastHourmeterUpdate: asset.lastHourmeterUpdate ?? (() => {
                    const lastUpdate = mailaAssets.find(el => el.vehicleId === asset.id)?.lastUpdate;
                    if (lastUpdate) {
                        return new Date(lastUpdate);
                    }
                    return undefined;
                })(),
            };
        }));
    }

    const addCheckups = async (data: Asset[]) => {
        const checkups = await fetchCheckupsByChassisList(data.map(asset => asset.chassis), data);
        setAssets(prev => prev.map((asset, index) => ({
            ...asset,
            schedulings: checkups.find(el => el.chassis === asset.chassis) ? {
                previous: checkups.find(el => el.chassis === asset.chassis)?.previous ?? [],
                next: checkups.find(el => el.chassis === asset.chassis)?.next ?? [],
            } : {
                previous: asset.schedulings?.previous ?? [],
                next: asset.schedulings?.next ?? []
            }
        })));
    }

    return { allSummaryAssets, assetIds, loading, loadMoreLoading, loadAssetsPaged, assets, getMoreAssets, page, assetIdsPaged, disableBigNumber }
}

export default useMaintenance;